// stores/customerStore.js
'use client';

import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  try {
    const response = await fleetApi.post(url + getTzParam(), payload);
    return response;
  } catch (err) {
    console.error('API fetcher error:', { url, payload, status: err?.response?.status, data: err?.response?.data });
    throw err;
  }
};
export const revalidateCustomerList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/customer/search');
};

export const useCustomerList = (page = 0, rowsPerPage = 1000, searchKey = 'name', searchValue = '', companyId = '') => {
  const start = page * rowsPerPage;

  const payload = useMemo(() => {
    const fb = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .setOrder(['name'], true)
      .addJoin('business_type', ['name'])
      .addJoin('company', ['name'])
      .addJoin('country', ['name']);

    const tableName = searchKey === 'business_type' ? 'm_business_type' : null;
    const filterLike = searchKey === 'business_type' ? 'name' : searchKey;

    if (searchValue && searchValue.trim() !== '') {
      fb.like(filterLike, searchValue, 'AND', tableName);
    }

    if (companyId) {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    } else {
      fb.filterByCompany();
    }
    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue, companyId]);

  const key = useMemo(() => ['/customer/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  return {
    data: data?.data || [],
    totalRecord: data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? 0,
    isLoading,
    isError: error
  };
};

export const getCustomerById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const resp = await fleetApi.get(`/customer/${id}${getTzParam()}`);
    if (resp?.data) return resp.data;
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).filterByCompany().equal('id', String(id));
    const payload = fb.build();
    const response = await fleetApi.post(`/customer/search${getTzParam()}`, payload);
    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);
    return Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
  } catch (err) {
    console.error('Failed to fetch customer by id:', err);
    throw err;
  }
};

export const createCustomer = async (formData = {}) => {
  try {
    const body = {
      ...formData
    };
    const response = await fleetApi.post(`/customer/new${getTzParam()}`, body);

    if (response?.status === false || response?.error === true || response?.code >= 400) {
      throw { response: { data: response } };
    }
    revalidateCustomerList();
    return response.data;
  } catch (err) {
    console.error('Error creating customer:', err);
    throw err;
  }
};

export const updateCustomer = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = {
      ...formData
    };
    const response = await fleetApi.put(`/customer/edit/${id}${getTzParam()}`, body);

    if (response?.status === false || response?.error === true || response?.code >= 400) {
      throw { response: { data: response } };
    }
    revalidateCustomerList();
    mutate(`/customer/${id}`);
    return response.data;
  } catch (err) {
    console.error('Error updating customer:', err);
    throw err;
  }
};

export const deleteCustomer = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/customer/delete/${id}${getTzParam()}`);

    if (response?.status === false || response?.error === true || response?.code >= 400) {
      throw { response: { data: response } };
    }
    revalidateCustomerList();
    return response.data;
  } catch (err) {
    console.error('Error deleting customer:', err);
    throw err;
  }
};
